import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Calendar, User, Tag, Clock, Search, ChevronRight, BookOpen } from 'lucide-react';
import Button from '../components/atoms/Button';
import Card from '../components/atoms/Card';
import Input from '../components/atoms/Input';
import ResponsiveContainer from '../components/molecules/ResponsiveContainer';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Sample blog posts
  const blogPosts = [
    {
      id: 'post-1',
      title: 'The Future of Document Security in Web3',
      excerpt: 'Exploring how blockchain technology is revolutionizing document security and verification in the digital age.',
      author: 'Glenn Rodney',
      date: 'June 10, 2025',
      category: 'Blockchain',
      tags: ['Security', 'Web3', 'Blockchain'],
      readTime: '8 min read',
      image: 'https://images.pexels.com/photos/8370752/pexels-photo-8370752.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      id: 'post-2',
      title: 'End-to-End Encryption: Why It Matters for Your Documents',
      excerpt: 'A deep dive into how end-to-end encryption protects your sensitive information from unauthorized access.',
      author: 'Glenn Rodney',
      date: 'May 28, 2025',
      category: 'Security',
      tags: ['Encryption', 'Privacy', 'Data Protection'],
      readTime: '6 min read',
      image: 'https://images.pexels.com/photos/5380642/pexels-photo-5380642.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      id: 'post-3',
      title: 'Digital Wills: Securing Your Legacy in the Digital Age',
      excerpt: 'How blockchain-based digital wills are transforming estate planning and providing unprecedented security.',
      author: 'Glenn Rodney',
      date: 'May 15, 2025',
      category: 'Digital Wills',
      tags: ['Estate Planning', 'Blockchain', 'Legal Tech'],
      readTime: '10 min read',
      image: 'https://images.pexels.com/photos/7176026/pexels-photo-7176026.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      id: 'post-4',
      title: 'IPFS and Decentralized Storage: The Future of Document Management',
      excerpt: 'Why decentralized storage solutions like IPFS are essential for truly secure and resilient document management.',
      author: 'Glenn Rodney',
      date: 'April 30, 2025',
      category: 'Technology',
      tags: ['IPFS', 'Decentralization', 'Storage'],
      readTime: '7 min read',
      image: 'https://images.pexels.com/photos/8438923/pexels-photo-8438923.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    },
    {
      id: 'post-5',
      title: 'Zero-Knowledge Proofs: The Next Frontier in Document Verification',
      excerpt: 'How zero-knowledge cryptography is enabling new ways to verify document authenticity without revealing sensitive information.',
      author: 'Glenn Rodney',
      date: 'April 15, 2025',
      category: 'Cryptography',
      tags: ['Zero-Knowledge', 'Cryptography', 'Verification'],
      readTime: '9 min read',
      image: 'https://images.pexels.com/photos/8353802/pexels-photo-8353802.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'
    }
  ];

  // Filter posts based on search query
  const filteredPosts = blogPosts.filter(post => 
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Categories for sidebar
  const categories = [
    { name: 'Blockchain', count: 2 },
    { name: 'Security', count: 3 },
    { name: 'Cryptography', count: 1 },
    { name: 'Technology', count: 2 },
    { name: 'Digital Wills', count: 1 }
  ];

  // Popular tags
  const popularTags = [
    { name: 'Security', count: 4 },
    { name: 'Blockchain', count: 3 },
    { name: 'Encryption', count: 2 },
    { name: 'Web3', count: 2 },
    { name: 'Privacy', count: 2 },
    { name: 'IPFS', count: 1 },
    { name: 'Zero-Knowledge', count: 1 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                icon={ArrowLeft}
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-gray-900"
              >
                Back to Home
              </Button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Blog</h1>
                  <p className="text-gray-600">Insights on security, blockchain, and document management</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <ResponsiveContainer maxWidth="full" padding="none">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Search */}
              <div className="mb-8">
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  icon={Search}
                />
              </div>

              {/* Featured Post */}
              {!searchQuery && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mb-8"
                >
                  <Card className="overflow-hidden">
                    <div className="relative h-80">
                      <img 
                        src="https://images.pexels.com/photos/8370752/pexels-photo-8370752.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1" 
                        alt="The Future of Document Security in Web3" 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex flex-col justify-end p-6">
                        <span className="text-white text-sm font-medium bg-primary-500 px-3 py-1 rounded-full inline-block mb-3">Featured</span>
                        <h2 className="text-2xl font-bold text-white mb-2">The Future of Document Security in Web3</h2>
                        <p className="text-gray-200 mb-4">Exploring how blockchain technology is revolutionizing document security and verification in the digital age.</p>
                        <div className="flex items-center text-gray-200 text-sm">
                          <span className="flex items-center gap-1 mr-4">
                            <User size={14} />
                            Glenn Rodney
                          </span>
                          <span className="flex items-center gap-1 mr-4">
                            <Calendar size={14} />
                            June 10, 2025
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            8 min read
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}

              {/* Blog Posts */}
              <div className="space-y-8">
                {filteredPosts.length > 0 ? (
                  filteredPosts.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                    >
                      <Card className="overflow-hidden">
                        <div className="md:flex">
                          <div className="md:w-1/3 h-48 md:h-auto">
                            <img 
                              src={post.image} 
                              alt={post.title} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="p-6 md:w-2/3">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium bg-primary-100 text-primary-800 px-2 py-1 rounded">
                                {post.category}
                              </span>
                              <span className="text-xs text-gray-500">
                                {post.readTime}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{post.title}</h3>
                            <p className="text-gray-600 mb-4">{post.excerpt}</p>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-sm text-gray-500">
                                <span className="flex items-center gap-1 mr-3">
                                  <User size={14} />
                                  {post.author}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {post.date}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-primary-600"
                                icon={ChevronRight}
                                iconPosition="right"
                              >
                                Read More
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))
                ) : (
                  <Card className="p-8 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No articles found</h3>
                    <p className="text-gray-600">Try adjusting your search terms or browse categories.</p>
                  </Card>
                )}
              </div>

              {/* Pagination */}
              {filteredPosts.length > 0 && (
                <div className="flex justify-center mt-12">
                  <nav className="flex items-center gap-1">
                    <Button variant="outline" size="sm" disabled>
                      Previous
                    </Button>
                    <Button variant="primary" size="sm">
                      1
                    </Button>
                    <Button variant="outline" size="sm">
                      2
                    </Button>
                    <Button variant="outline" size="sm">
                      3
                    </Button>
                    <Button variant="outline" size="sm">
                      Next
                    </Button>
                  </nav>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* About */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">About Our Blog</h3>
                <p className="text-gray-600 mb-4">
                  The Aegis Vault blog explores the latest in document security, blockchain technology, and digital estate planning. Written by our team at GravexLabs, led by Glenn Rodney.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/about')}
                  className="w-full"
                >
                  Learn More About Us
                </Button>
              </Card>

              {/* Categories */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
                <ul className="space-y-2">
                  {categories.map(category => (
                    <li key={category.name}>
                      <button className="flex items-center justify-between w-full p-2 hover:bg-gray-50 rounded-lg transition-colors">
                        <span className="text-gray-700">{category.name}</span>
                        <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                          {category.count}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Popular Tags */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Popular Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map(tag => (
                    <button
                      key={tag.name}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-sm text-gray-700 transition-colors"
                    >
                      <Tag size={14} />
                      {tag.name}
                      <span className="text-xs text-gray-500">({tag.count})</span>
                    </button>
                  ))}
                </div>
              </Card>

              {/* Newsletter */}
              <Card className="p-6 bg-gradient-to-br from-primary-50 to-secondary-50">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscribe to Our Newsletter</h3>
                <p className="text-gray-600 mb-4">
                  Get the latest articles on security, blockchain, and document management delivered to your inbox.
                </p>
                <div className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Your email address"
                  />
                  <Button
                    variant="primary"
                    className="w-full"
                  >
                    Subscribe
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  We respect your privacy. Unsubscribe at any time.
                </p>
              </Card>
            </div>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BlogPage;